/**
 * Dedicated axios instance for the standalone kiosk page.
 *
 * Unlike the main `api.js` client, this one:
 *   - Injects `X-Kiosk-Token` on every request (never JWT)
 *   - Has NO refresh-token logic (kiosk tokens are long-lived)
 *   - Targets ONLY `/kiosk/*` endpoints (every other endpoint in the
 *     app will 403 a kiosk token server-side anyway, but we also gate
 *     client-side for clarity)
 *
 * The token is captured from the URL once at page mount and kept in the
 * instance's default headers. This instance is created per kiosk page
 * load by `createKioskApi(token)`.
 */
import axios from 'axios';

export const createKioskApi = (token) => {
  const instance = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: {
      'X-Kiosk-Token': token,
    },
    params: {
      ts: new Date().getTime(),
    },
  });

  // Safety net: reject any request that isn't a /kiosk/* path. If code
  // is ever refactored and accidentally points a kiosk instance at a
  // regular endpoint, this fail-closed behavior keeps the blast radius
  // small. The backend would 403 anyway; this just makes the failure
  // louder and more obvious during dev.
  instance.interceptors.request.use((config) => {
    const url = config.url || '';
    if (!url.startsWith('/kiosk/')) {
      return Promise.reject(
        new Error(
          `kioskApi refused a non-/kiosk/ request: ${config.method?.toUpperCase() || 'GET'} ${url}`
        )
      );
    }
    return config;
  });

  return instance;
};
